import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import arLocale from "../i18n/locales/ar.json";
import enLocale from "../i18n/locales/en.json";
import { getLangFromPathname, getPreferredLang, normalizeLang, swapLangInPath } from "../lib/routes";

const DASHBOARD_LANG_KEY = "tns:dashboard_language";

const I18nContext = createContext({
  language: "ar",
  setLanguage: () => {},
  t: (arText, enText) => (enText ?? arText),
  tr: (key, fallbackEn) => fallbackEn ?? key,
});

const getNestedValue = (source, path) => {
  const target = source && typeof source === "object" ? source : null;
  if (!target || !path) return undefined;
  return String(path)
    .split(".")
    .reduce((acc, segment) => {
      if (!acc || typeof acc !== "object") return undefined;
      return acc[segment];
    }, target);
};

export const I18nProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguage] = useState(() => getPreferredLang());

  useEffect(() => {
    const routeLang = getLangFromPathname(location.pathname);
    if (routeLang) {
      const normalizedRouteLang = normalizeLang(routeLang);
      if (normalizedRouteLang !== language) setLanguage(normalizedRouteLang);
      localStorage.setItem(DASHBOARD_LANG_KEY, normalizedRouteLang);
      return;
    }
    const preferred = getPreferredLang();
    if (preferred !== language) setLanguage(preferred);
  }, [language, location.pathname]);

  useEffect(() => {
    document.documentElement.lang = language === "en" ? "en" : "ar";
    document.documentElement.dir = language === "en" ? "ltr" : "rtl";
  }, [language]);

  const t = useCallback(
    (arText, enText) => (language === "en" ? (enText ?? arText) : arText),
    [language],
  );

  const tr = useCallback(
    (key, fallbackEn) => {
      const fromAr = getNestedValue(arLocale, key);
      const fromEn = getNestedValue(enLocale, key);
      const preferred = language === "en" ? fromEn ?? fromAr : fromAr ?? fromEn;
      if (typeof preferred === "string" && preferred.trim()) return preferred;
      if (typeof fallbackEn === "string" && fallbackEn.trim()) return fallbackEn;
      return String(key || "");
    },
    [language],
  );

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage: (lang) => {
          const next = normalizeLang(lang);
          localStorage.setItem(DASHBOARD_LANG_KEY, next);
          setLanguage(next);
          const nextPath = swapLangInPath(location.pathname, next);
          const nextUrl = `${nextPath}${location.search || ""}${location.hash || ""}`;
          const currentUrl = `${location.pathname}${location.search || ""}${location.hash || ""}`;
          if (nextUrl !== currentUrl) {
            navigate(nextUrl, { replace: false });
          }
        },
        t,
        tr,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
